/*
 * The ore heatmap's texture, worked out from the findings: a pure function
 * (no renderer, no DOM), so it runs in a worker (oreWorker.ts) and a
 * survey's update doesn't stall a frame. Moved as it was from the
 * renderer.
 */

import type { OreFinding } from "./renderer";

type RGB = [number, number, number];

export interface OreJob {
  f: { list: OreFinding[]; cols: number; rows: number; cell: number };
  ores: string[];
  hues: RGB[];
  unknown: RGB;
  W: number;
  H: number;
}

export interface OreTexture {
  TW: number;
  TH: number;
  res: number;
  data: Uint8Array;
}

let floatsPool = new Float32Array(0);
function pooledFloats(n: number): Float32Array {
  if (floatsPool.length < n) floatsPool = new Float32Array(n);
  return floatsPool.subarray(0, n);
}

export function oreField({ f, ores, hues, unknown, W, H }: OreJob): OreTexture {
  const { cols, rows, cell } = f;
  // A texel a hex on a small map, 2x2 hexes on a big one (a quarter of
  // the memory: a 2048-hex map's heatmap at full size was tens of MB a
  // build, enough to run a long session out of memory).
  const res = W * H > 1024 * 1024 ? 2 : 1;
  const [TW, TH] = [Math.ceil(W / res), Math.ceil(H / res)];
  // Per cell: each ore's heat (0..1), and of ore of unknown kind.
  const kc = ores.map(() => new Float32Array(cols * rows));
  const uc = new Float32Array(cols * rows);
  const of = (x: OreFinding, ore: string) => x.ores.filter((o) => o.ore === ore).reduce((a, o) => a + o.amount, 0);
  const known = (x: OreFinding) => x.ores.length > 0 || !!x.grid || !!x.grid4;
  const total = (x: OreFinding) => (known(x) ? ores.reduce((a, o) => a + of(x, o), 0) : (x.total?.[0] ?? 0));
  const top = Math.max(1, ...f.list.map(total));
  let [q0, r0, q1, r1] = [W, H, 0, 0];
  for (const x of f.list) {
    if (x.cell >= cols * rows) continue;
    if (known(x)) ores.forEach((o, n) => (kc[n][x.cell] = Math.sqrt(of(x, o) / top)));
    else uc[x.cell] = Math.sqrt(total(x) / top);
    const [cq, cr] = [(x.cell % cols) * cell, Math.floor(x.cell / cols) * cell];
    [q0, r0, q1, r1] = [Math.min(q0, cq - cell), Math.min(r0, cr - cell), Math.max(q1, cq + 2 * cell), Math.max(r1, cr + 2 * cell)];
  }
  [q0, r0, q1, r1] = [Math.max(0, q0), Math.max(0, r0), Math.min(W, q1), Math.min(H, r1)];
  // The texture's bytes: pooled (grown, never thrown away), cleared.
  const data = new Uint8Array(TW * TH * 4);
  if (q1 <= q0 || r1 <= r0) return { TW, TH, res, data };
  // The fields over the known ground's box (and a cell round it), in
  // texels; each ore's field, the unknown one and the blur's scratch are
  // views into one pooled buffer.
  const [t0, s0] = [Math.floor(q0 / res), Math.floor(r0 / res)];
  const bw = Math.ceil(q1 / res) - t0;
  const bh = Math.ceil(r1 / res) - s0;
  const floats = pooledFloats((ores.length + 2) * bw * bh);
  const K = ores.map((_, n) => floats.subarray(n * bw * bh, (n + 1) * bw * bh));
  const U = floats.subarray(ores.length * bw * bh, (ores.length + 1) * bw * bh);
  const tmp = floats.subarray((ores.length + 1) * bw * bh, (ores.length + 2) * bw * bh);
  const lerp2 = (g: Float32Array, n: number, m: number, fx: number, fy: number) => {
    const [x0, y0] = [Math.floor(fx), Math.floor(fy)];
    const [x1, y1] = [Math.min(n - 1, x0 + 1), Math.min(m - 1, y0 + 1)];
    const [tx, ty] = [fx - x0, fy - y0];
    return (g[y0 * n + x0] * (1 - tx) + g[y0 * n + x1] * tx) * (1 - ty) + (g[y1 * n + x0] * (1 - tx) + g[y1 * n + x1] * tx) * ty;
  };
  // Between cell centres (each texel sampled at its first hex).
  const centre = (v: number, n: number) => Math.min(n - 1, Math.max(0, (v + 0.5) / cell - 0.5));
  for (let y = 0; y < bh; y++) {
    const fy = centre((y + s0) * res, rows);
    for (let x = 0; x < bw; x++) {
      const fx = centre((x + t0) * res, cols);
      const i = y * bw + x;
      for (let n = 0; n < ores.length; n++) K[n][i] = lerp2(kc[n], cols, rows, fx, fy);
      U[i] = lerp2(uc, cols, rows, fx, fy);
    }
  }
  // Sampled cells: each ore's cell heat shared out by each texel's grade
  // of it, soil's sixteenths blended.
  for (const x of f.list) {
    const fine = x.grid ? { g: x.grid, n: cell } : x.grid4 ? { g: x.grid4, n: 4 } : null;
    if (!fine || x.cell >= cols * rows) continue;
    const n = fine.n;
    const [cq, cr] = [(x.cell % cols) * cell, Math.floor(x.cell / cols) * cell];
    const step = cell / n;
    const sub = (v: number) => Math.min(n - 1, Math.max(0, (v + 0.5) / step - 0.5));
    ores.forEach((ore, o) => {
      const grades = new Float32Array(n * n);
      for (let j = 0; j < n * n; j++) grades[j] = fine.g[ore]?.[j] ?? 0;
      let mean = 0;
      const texels: [number, number][] = [];
      for (let ty = Math.floor(cr / res); ty * res < Math.min(r1, cr + cell); ty++) {
        for (let tx = Math.floor(cq / res); tx * res < Math.min(q1, cq + cell); tx++) {
          const [lx, ly] = [tx - t0, ty - s0];
          if (lx < 0 || ly < 0 || lx >= bw || ly >= bh) continue;
          const g = lerp2(grades, n, n, sub(tx * res - cq), sub(ty * res - cr));
          mean += g;
          texels.push([ly * bw + lx, g]);
        }
      }
      mean /= Math.max(1, texels.length);
      const k = kc[o][x.cell];
      for (const [i, g] of texels) K[o][i] = mean > 0 ? Math.min(1, (k * g) / mean) : 0;
    });
  }
  // A light blur (two box passes each way) smooths what's left of edges
  // (a texel's reach: half as far in texels on a big map).
  const R = res === 1 ? 2 : 1;
  const blur = (a: Float32Array) => {
    for (let pass = 0; pass < 2; pass++) {
      for (let y = 0; y < bh; y++) {
        for (let x = 0; x < bw; x++) {
          let sum = 0;
          for (let d = -R; d <= R; d++) sum += a[y * bw + Math.min(bw - 1, Math.max(0, x + d))];
          tmp[y * bw + x] = sum / (2 * R + 1);
        }
      }
      for (let y = 0; y < bh; y++) {
        for (let x = 0; x < bw; x++) {
          let sum = 0;
          for (let d = -R; d <= R; d++) sum += tmp[Math.min(bh - 1, Math.max(0, y + d)) * bw + x];
          a[y * bw + x] = sum / (2 * R + 1);
        }
      }
    }
  };
  K.forEach(blur);
  blur(U);
      for (let y = 0; y < bh; y++) {
    for (let x = 0; x < bw; x++) {
      const j = y * bw + x;
      // The ores' hues, the strongest ore's dominating (weighted by heat
      // to the fourth: with sixteen ores a plain mix is grey mud; two
      // deposits alike still show both); richer brighter and more
      // opaque, little fading into the ground.
      let sum = 0;
      let wsum = 0;
      let c0 = 0;
      let c1 = 0;
      let c2 = 0;
      for (let n = 0; n < K.length; n++) {
        const v = K[n][j];
        sum += v;
        const w = v * v * v * v;
        wsum += w;
        c0 += hues[n][0] * w;
        c1 += hues[n][1] * w;
        c2 += hues[n][2] * w;
      }
      const k = Math.min(1, sum);
      const lit = wsum > 0 ? (0.55 + 0.45 * k) / wsum : 0;
      const ak = 0.9 * Math.pow(k, 0.6);
      const au = 0.32 * Math.sqrt(U[j]) * (1 - ak);
      const i = ((y + s0) * TW + x + t0) * 4;
      data[i] = (c0 * lit * ak + unknown[0] * au) * 255;
      data[i + 1] = (c1 * lit * ak + unknown[1] * au) * 255;
      data[i + 2] = (c2 * lit * ak + unknown[2] * au) * 255;
      data[i + 3] = (ak + au) * 255;
    }
  }
  return { TW, TH, res, data };
}
